import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { Pagination } from '@material-ui/lab';
import { useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import cuid from 'cuid';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from '@super-template/lab/src/ErrorFallback';

import {
  Button,
  ClickAwayListener,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import messages from 'messages.js';
import { editUsagePeriod, fetchUsagePeriods, getSectionForManager } from 'api';
import DeleteButton from '../DeleteButton';
import OrgAdminActionDialog from 'components/OrgAdminActionDialog/OrgAdminActionDialog';
import { SnackbarVariant } from '../../../constants.js';
import { useSnackbar } from 'notistack';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp.js';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown.js';

const useStyles = makeStyles((theme) => ({
  tableHeader: {
    height: '50px',
    background: theme.palette.common.greyLight,
    color: theme.palette.common.black,
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  icon: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    fontSize: '1.5rem',
    lineHeight: '1',
    verticalAlign: 'middle',
  },
  tabletitle: {
    marginLeft: theme.spacing(1),
    fontSize: '1.5rem',
    lineHeight: '1',
    verticalAlign: 'middle',
  },
  sectiontable: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.common.white,
    },
  },
  tableRow: {
    border: '1px solid rgba(0,0,0,0.2)',
  },
  tableCell: {
    padding: '10px 10px',
  },
  tableButton: {
    color: theme.palette.hallinto.blueDark,
  },
  actionFooter: {
    marginTop: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

const UsagePeriodHeader = () => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <div className={classes.tableHeader}>
      <Typography variant="h2" color="primary" className={classes.tabletitle}>
        <ListAltIcon className={classes.icon} />
        {intl.formatMessage(messages.usagePeriod_title)}
      </Typography>
    </div>
  );
};

export const UsagePeriodList = () => {
  const classes = useStyles();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const { sectionId } = useParams();
  const [usagePeriodList, setUsagePeriodList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUsagePeriod, setSelectedUsagePeriod] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [dialogType, setDialogType] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  useEffect(() => {
    getUsagePeriodList();
    // eslint-disable-next-line
  }, [location]);

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = React.useRef({}); // store refs to each button

  const getUsagePeriodList = () => {
    getSectionForManager(sectionId)
      .then((sectionResponse) => {
        fetchUsagePeriods(
          sectionResponse.data.serviceCode,
          sectionResponse.data.title
        )
          .then((usagePeriodsResponse) => {
            setUsagePeriodList(usagePeriodsResponse.data);
          })
          .catch(() => {
            enqueueSnackbar(
              intl.formatMessage(messages.usagePeriod_usageperiods_fetch_error),
              {
                variant: SnackbarVariant.ERROR,
              }
            );
          });
      })
      .catch(() => {
        enqueueSnackbar(
          intl.formatMessage(messages.usagePeriod_section_fetch_error),
          {
            variant: SnackbarVariant.ERROR,
          }
        );
      });
  };

  const handleDelete = (account, section) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    const endedUsagePeriod = {
      ...section,
      EndDate: yesterday.toISOString().split('T')[0],
      Method: 'disable',
    };

    const indexOfEndedUsagePeriod = account.sections.findIndex(
      (s) => s.id === section.id
    );
    account.sections[indexOfEndedUsagePeriod] = endedUsagePeriod;

    editUsagePeriod(account)
      .then(() => {
        enqueueSnackbar(
          intl.formatMessage(messages.usagePeriod_ended_success),
          {
            variant: SnackbarVariant.SUCCESS,
          }
        );
      })
      .catch(() => {
        enqueueSnackbar(intl.formatMessage(messages.usagePeriod_ended_error), {
          variant: SnackbarVariant.ERROR,
        });
      });
  };

  const handleEditClick = (account, section) => {
    setSelectedUsagePeriod(section);
    setSelectedAccount(account);
    setDialogType('usagePeriod');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  // Weird implementation, but Menu/MenuList did not work with an async list map
  const handleToggle = (id) => {
    const button = buttonRefs.current[id];
    if (!button) return;

    const rect = button.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const renderUsagePeriod = (account) => {
    return account.sections.map((section) => {
      return (
        <TableRow className={classes.tableRow} key={cuid()}>
          <TableCell className={classes.tableCell}>{account.SSN}</TableCell>
          <TableCell className={classes.tableCell}>
            {account.Etunimi + ' ' + account.Sukunimi}
          </TableCell>
          <TableCell className={classes.tableCell}>{section.EndDate}</TableCell>
          <TableCell className={classes.tableCell}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              ref={(el) => (buttonRefs.current[account.SSN] = el)}
              onClick={() => handleToggle(account.SSN)}
            >
              {intl.formatMessage(messages.general_actions)}
              {openDropdownId === account.SSN ? (
                <KeyboardArrowUpIcon
                  style={{
                    fontSize: 30,
                    position: 'relative',
                    top: '0px',
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  style={{
                    fontSize: 30,
                    position: 'relative',
                    top: '0px',
                  }}
                />
              )}
            </Button>
            {openDropdownId === account.SSN && (
              <ClickAwayListener onClickAway={() => setOpenDropdownId(null)}>
                <Paper
                  style={{
                    position: 'absolute',
                    top: dropdownPosition.top + 2,
                    left: dropdownPosition.left,
                    zIndex: 10,
                    borderRadius: 6,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    maxWidth: '185px',
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{
                      padding: '8px 16px',
                      marginBottom: '2px',
                      minWidth: '185px',
                      textAlign: 'center',
                    }}
                    onClick={() => {
                      handleEditClick(account, section);
                      setOpenDropdownId(null);
                    }}
                  >
                    {intl.formatMessage(messages.general_edit)}
                  </Button>

                  <DeleteButton
                    id={account.SSN}
                    handleDeleteCallback={() => {
                      handleDelete(account, section);
                      setOpenDropdownId(null);
                    }}
                    style={{
                      padding: '8px 16px',
                      marginBottom: '2px',
                      minWidth: '185px',
                      textAlign: 'center',
                    }}
                    className={classes.button}
                  >
                    {intl.formatMessage(messages.general_delete)}
                  </DeleteButton>
                </Paper>
              </ClickAwayListener>
            )}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className={classes.sectiontable}>
        <UsagePeriodHeader />
        <TableContainer>
          <Table className={classes.sectiontable}>
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  {intl.formatMessage(messages.sso)}
                </TableCell>
                <TableCell align="left">
                  {intl.formatMessage(messages.name)}
                </TableCell>
                <TableCell align="left">
                  {intl.formatMessage(messages.usagePeriod_expiry_date)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usagePeriodList
                .slice((page - 1) * PER_PAGE, page * PER_PAGE)
                .map((account) => renderUsagePeriod(account))}
            </TableBody>
          </Table>
        </TableContainer>
        <div className={classes.actionFooter}>
          <Pagination
            count={Math.ceil(usagePeriodList.length / PER_PAGE)}
            page={page}
            onChange={(event, value) => setPage(value)}
          />
        </div>
        <OrgAdminActionDialog
          dialogType={dialogType}
          open={dialogOpen}
          uploadType={'usagePeriod'}
          handleClose={handleCloseDialog}
          usagePeriod={selectedUsagePeriod}
          account={selectedAccount}
          sectionId={sectionId}
          isEditing={selectedUsagePeriod !== null}
          updateListCallback={() => getUsagePeriodList()}
        />
      </div>
    </ErrorBoundary>
  );
};
